import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

function pad(n) {
  return String(n).padStart(2, '0');
}

export function formatHMS(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return `${pad(m)}:${pad(sec)}`;
}

/** Parse DB / PHP-style time labels (MM:SS or HH:MM:SS) to seconds. */
export function parseTimeLabel(label) {
  if (label == null || label === '') return null;
  const parts = String(label).trim().split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

function timerStorageKey(userId, sessionId) {
  if (!userId || !sessionId) return null;
  return `ic_totaltime_${sessionId}_${userId}`;
}

/** Remove legacy unscoped key that leaked time across sessions. */
export function clearLegacyTimerStorage() {
  try {
    sessionStorage.removeItem('totaltime');
  } catch {
    /* ignore */
  }
}

export function readStoredElapsedSeconds(userId, sessionId) {
  clearLegacyTimerStorage();
  const key = timerStorageKey(userId, sessionId);
  if (!key) return 0;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored != null && stored !== '') return Math.max(0, parseInt(stored, 10) || 0);
  } catch {
    /* ignore */
  }
  return 0;
}

export function writeStoredElapsedSeconds(seconds, userId, sessionId) {
  const key = timerStorageKey(userId, sessionId);
  if (!key) return 0;
  const sec = Math.max(0, Math.floor(Number(seconds) || 0));
  try {
    sessionStorage.setItem(key, String(sec));
  } catch {
    /* ignore */
  }
  return sec;
}

/**
 * Carnival cumulative timer scoped per user + session.
 * @param {{ running?: boolean, seedSeconds?: number|null }} options
 */
export function useCarnivalTimer(options = {}) {
  const { running = true, seedSeconds = null } = options;
  const user = useSelector((s) => s.auth.user);
  const userId = user?.userId;
  const sessionId = user?.sessionId;

  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);

  useEffect(() => {
    if (!userId || !sessionId) {
      elapsedRef.current = 0;
      setElapsed(0);
      return;
    }
    clearLegacyTimerStorage();
    const stored = readStoredElapsedSeconds(userId, sessionId);
    elapsedRef.current = stored;
    setElapsed(stored);
  }, [userId, sessionId]);

  useEffect(() => {
    if (seedSeconds == null || !userId || !sessionId) return;
    const sec = writeStoredElapsedSeconds(seedSeconds, userId, sessionId);
    elapsedRef.current = sec;
    setElapsed(sec);
  }, [seedSeconds, userId, sessionId]);

  useEffect(() => {
    if (!userId || !sessionId) return undefined;

    if (!running) {
      const stored = readStoredElapsedSeconds(userId, sessionId);
      elapsedRef.current = stored;
      setElapsed(stored);
      return undefined;
    }

    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      writeStoredElapsedSeconds(elapsedRef.current, userId, sessionId);
    }, 1000);

    return () => clearInterval(id);
  }, [running, userId, sessionId]);

  return { elapsed, elapsedRef, display: formatHMS(elapsed) };
}
